import React, { useState, useEffect, useCallback } from 'react';
import './UGStudentDashboard.css';
import collegeConfig from '../../config/collegeConfig';
import { FaChevronLeft, FaChalkboardTeacher, FaPlus, FaTimes, FaPencilAlt, FaTrash, FaFilePdf, FaFileImage, FaDownload, FaCheckCircle } from 'react-icons/fa';
import { saveFile } from '../../utils/saveFile';
import CertUploadWidget from '../shared/CertUploadWidget';
import Pagination, { ITEMS_PER_PAGE } from './Pagination';

const EVENT_TYPES = ['Conference', 'Workshop', 'Seminar', 'CME', 'Symposium', 'Webinar', 'Other'];
const MODES = ['In-person', 'Online', 'Hybrid'];
const ROLES = ['Attendee', 'Poster Presenter', 'Paper Presenter', 'Resource Person', 'Organizer'];

const emptyForm = () => ({
  title: '',
  eventType: 'Conference',
  mode: 'In-person',
  organizer: '',
  startDate: '',
  endDate: '',
  location: '',
  role: 'Attendee',
  topic: '',
  keyLearnings: '',
  additionalNotes: '',
  certificateReceived: false,
  certificate: null
});

const formatDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const UGConference = ({ onBack }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalEntry, setModalEntry] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const token = localStorage.getItem('token');
  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/conference/mine`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setEntries(data.entries || []);
    } catch (err) {
      console.error('Error fetching conference entries:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const openNew = () => { setForm(emptyForm()); setModalEntry('new'); };
  const openEdit = (entry) => {
    setForm({
      title: entry.title,
      eventType: entry.eventType,
      mode: entry.mode,
      organizer: entry.organizer || '',
      startDate: entry.startDate ? new Date(entry.startDate).toISOString().slice(0, 10) : '',
      endDate: entry.endDate ? new Date(entry.endDate).toISOString().slice(0, 10) : '',
      location: entry.location || '',
      role: entry.role,
      topic: entry.topic || '',
      keyLearnings: entry.keyLearnings || '',
      additionalNotes: entry.additionalNotes || '',
      certificateReceived: !!entry.certificateReceived,
      certificate: entry.certificate || null
    });
    setModalEntry(entry);
  };

  const submit = async () => {
    if (!form.title.trim()) {
      alert('Please fill in Title / Event name before saving.');
      return;
    }
    const isEdit = modalEntry && modalEntry !== 'new';
    try {
      setSubmitting(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/conference${isEdit ? `/${modalEntry._id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        setModalEntry(null);
        await fetchEntries();
      } else {
        alert(data.message || 'Failed to save conference entry');
      }
    } catch (err) {
      console.error('Error saving conference entry:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (entry) => {
    if (!window.confirm('Delete this conference entry? This cannot be undone.')) return;
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/conference/${entry._id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchEntries();
      else alert(data.message || 'Failed to delete');
    } catch (err) {
      console.error('Error deleting conference entry:', err);
    }
  };

  // Explicit resubmit after a "needs revision" decision — editing the
  // entry alone does not flip its review status back to pending.
  const resubmit = async (entry) => {
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/conference/${entry._id}/resubmit`, { method: 'PUT', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchEntries();
      else alert(data.message || 'Failed to resubmit');
    } catch (err) {
      console.error('Error resubmitting conference entry:', err);
    }
  };

  const downloadCertificate = async (entry) => {
    setDownloadingId(entry._id);
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/conference/${entry._id}/certificate/download`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        await saveFile(data.data, data.mimeType, data.fileName || 'certificate');
      } else {
        alert(data.message || 'Failed to download certificate');
      }
    } catch (err) {
      console.error('Error downloading certificate:', err);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleCertUpload = ({ fileKey, fileName, mimeType }) => {
    setForm(f => ({ ...f, certificateReceived: true, certificate: { fileKey, fileName, mimeType } }));
  };

  const eventCount = entries.length;
  const certificateCount = entries.filter(e => e.certificateReceived && e.certificate?.fileKey).length;

  return (
    <main className="ug-main">
      <button className="ug-back-btn" onClick={onBack}><FaChevronLeft /> Back to Dashboard</button>
      <h2 className="ug-section-title"><FaChalkboardTeacher /> Conference / Seminars</h2>
      <p className="ug-modal-hint" style={{ margin: '0 0 14px 0' }}>
        Log external conferences, workshops, and seminars you have attended or presented at.
      </p>

      <div className="ug-conf-stats">
        <div className="ug-conf-stat-box">
          <div className="ug-conf-stat-num">{eventCount}</div>
          <div className="ug-conf-stat-label">Events</div>
        </div>
        <div className="ug-conf-stat-box">
          <div className="ug-conf-stat-num">{certificateCount}</div>
          <div className="ug-conf-stat-label">Certificates</div>
        </div>
      </div>

      <button className="ug-btn-primary ug-reflection-add-btn" onClick={openNew}>
        <FaPlus /> Add entry
      </button>

      {loading ? (
        <div className="ug-due-empty">Loading conference entries...</div>
      ) : entries.length === 0 ? (
        <div className="ug-due-empty">
          No conference entries yet. Log your attendance at external conferences, workshops, and seminars.
        </div>
      ) : (
        <>
        <div className="ug-reflection-list">
          {entries.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map(entry => (
            <div key={entry._id} className="ug-reflection-card">
              <div className="ug-reflection-card-top">
                <span className="ug-reflection-type-badge ug-reflection-type-weekly">{entry.eventType}</span>
                <span className="ug-reflection-date">{formatDate(entry.startDate)}{entry.endDate ? ` – ${formatDate(entry.endDate)}` : ''}</span>
                {entry.review?.status === 'approved' && <span className="ug-status-badge ug-status-approved"><FaCheckCircle /> Approved</span>}
                {entry.review?.status === 'needs_revision' && <span className="ug-status-badge ug-status-revision">Needs revision</span>}
              </div>
              <h4 className="ug-reflection-period">{entry.title}</h4>
              <div className="ug-submission-meta" style={{ marginTop: 0 }}>
                <span>Mode: {entry.mode}</span>
                <span>Role: {entry.role}</span>
                {entry.organizer && <span>Organizer: {entry.organizer}</span>}
                {entry.location && <span>Location: {entry.location}</span>}
              </div>

              {entry.topic && (
                <>
                  <div className="ug-reflection-section-label">Topic / Theme</div>
                  <p className="ug-reflection-text">{entry.topic}</p>
                </>
              )}
              {entry.keyLearnings && (
                <>
                  <div className="ug-reflection-section-label">Key Learnings</div>
                  <p className="ug-reflection-text">{entry.keyLearnings}</p>
                </>
              )}
              {entry.additionalNotes && (
                <>
                  <div className="ug-reflection-section-label">Additional Notes</div>
                  <p className="ug-reflection-text">{entry.additionalNotes}</p>
                </>
              )}
              {(entry.review?.feedback || entry.facultyFeedback) && (
                <>
                  <div className="ug-reflection-section-label">Faculty Feedback</div>
                  <p className="ug-reflection-text ug-reflection-faculty-feedback">{entry.review?.feedback || entry.facultyFeedback}</p>
                </>
              )}
              {entry.review?.reviewerName && (
                <p className="ug-reviewer-tag">
                  {entry.review.status === 'approved' ? 'Verified by' : 'Reviewed by'} {entry.review.reviewerName}
                </p>
              )}

              {entry.certificateReceived && entry.certificate?.fileKey && (
                <div className="ug-conf-cert-row">
                  {entry.certificate.mimeType === 'application/pdf'
                    ? <FaFilePdf style={{ color: '#dc2626', fontSize: 18, flexShrink: 0 }} />
                    : <FaFileImage style={{ color: '#2563eb', fontSize: 18, flexShrink: 0 }} />}
                  <span className="ug-conf-cert-name">{entry.certificate.fileName}</span>
                  <button
                    className="ug-reflection-icon-btn"
                    onClick={() => downloadCertificate(entry)}
                    disabled={downloadingId === entry._id}
                    title="Download certificate"
                  >
                    <FaDownload /> {downloadingId === entry._id ? 'Downloading...' : 'Download'}
                  </button>
                </div>
              )}

              <div className="ug-reflection-card-actions">
                {entry.review?.status !== 'approved' && (
                  <>
                    <button className="ug-reflection-icon-btn" onClick={() => openEdit(entry)} title="Edit"><FaPencilAlt /></button>
                    <button className="ug-reflection-icon-btn ug-reflection-icon-btn-danger" onClick={() => remove(entry)} title="Delete"><FaTrash /></button>
                  </>
                )}
                {entry.review?.status === 'needs_revision' && (
                  <button className="ug-log-btn" onClick={() => resubmit(entry)}>Resubmit for review</button>
                )}
              </div>
            </div>
          ))}
        </div>
        <Pagination currentPage={currentPage} totalCount={entries.length} onPageChange={setCurrentPage} />
        </>
      )}

      {modalEntry && (
        <div className="ug-modal-overlay" onClick={() => !submitting && setModalEntry(null)}>
          <div className="ug-modal" onClick={e => e.stopPropagation()}>
            <div className="ug-modal-head">
              <h3>Log conference / seminar</h3>
              <FaTimes onClick={() => !submitting && setModalEntry(null)} />
            </div>

            <label className="ug-form-label">Title / Event name</label>
            <input
              type="text"
              className="ug-form-input"
              placeholder="e.g. National Conference on Emergency Medicine"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
            />

            <label className="ug-form-label">Event type</label>
            <select
              className="ug-form-input"
              value={form.eventType}
              onChange={e => setForm({ ...form, eventType: e.target.value })}
            >
              {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>

            <label className="ug-form-label">Mode</label>
            <select
              className="ug-form-input"
              value={form.mode}
              onChange={e => setForm({ ...form, mode: e.target.value })}
            >
              {MODES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>

            <label className="ug-form-label">Organizer (optional)</label>
            <input
              type="text"
              className="ug-form-input"
              value={form.organizer}
              onChange={e => setForm({ ...form, organizer: e.target.value })}
            />

            <label className="ug-form-label">Start date (optional)</label>
            <input
              type="date"
              className="ug-form-input"
              value={form.startDate}
              onChange={e => setForm({ ...form, startDate: e.target.value })}
            />

            <label className="ug-form-label">End date (optional)</label>
            <input
              type="date"
              className="ug-form-input"
              value={form.endDate}
              onChange={e => setForm({ ...form, endDate: e.target.value })}
            />

            <label className="ug-form-label">Location (optional)</label>
            <input
              type="text"
              className="ug-form-input"
              value={form.location}
              onChange={e => setForm({ ...form, location: e.target.value })}
            />

            <label className="ug-form-label">Your role</label>
            <select
              className="ug-form-input"
              value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value })}
            >
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>

            <label className="ug-form-label">Topic / theme (optional)</label>
            <input
              type="text"
              className="ug-form-input"
              value={form.topic}
              onChange={e => setForm({ ...form, topic: e.target.value })}
            />

            <label className="ug-form-label">Key learnings (optional)</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              value={form.keyLearnings}
              onChange={e => setForm({ ...form, keyLearnings: e.target.value })}
            />

            <label className="ug-form-label">Additional notes (optional)</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              value={form.additionalNotes}
              onChange={e => setForm({ ...form, additionalNotes: e.target.value })}
            />

            <label className="ug-conf-checkbox-row">
              <input
                type="checkbox"
                checked={form.certificateReceived}
                onChange={e => {
                  const checked = e.target.checked;
                  setForm(f => ({ ...f, certificateReceived: checked, certificate: checked ? f.certificate : null }));
                }}
              />
              Received participation certificate
            </label>

            {form.certificateReceived && (
              <>
                <label className="ug-form-label">Certificate PDF (optional)</label>
                {form.certificate?.fileKey ? (
                  <div className="ug-conf-cert-row">
                    {form.certificate.mimeType === 'application/pdf'
                      ? <FaFilePdf style={{ color: '#dc2626', fontSize: 18, flexShrink: 0 }} />
                      : <FaFileImage style={{ color: '#2563eb', fontSize: 18, flexShrink: 0 }} />}
                    <span className="ug-conf-cert-name">{form.certificate.fileName}</span>
                    <button
                      className="ug-reflection-icon-btn ug-reflection-icon-btn-danger"
                      onClick={() => setForm(f => ({ ...f, certificate: null }))}
                      title="Remove"
                    >
                      <FaTrash />
                    </button>
                  </div>
                ) : (
                  <CertUploadWidget
                    onUpload={handleCertUpload}
                    uploading={false}
                    inputId="ug-conf-cert-upload"
                    label="Tap to upload PDF"
                  />
                )}
              </>
            )}

            <div className="ug-modal-actions">
              <button className="ug-btn-secondary" onClick={() => setModalEntry(null)} disabled={submitting}>Cancel</button>
              <button className="ug-btn-primary" onClick={submit} disabled={submitting}>
                {submitting ? 'Saving...' : 'Save entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default UGConference;
